import { createContext, useContext, useState, useCallback, useEffect } from "react";

const OnboardingContext = createContext(null);

// Pricing Structure
export const PRICING = {
  packages: {
    starter: {
      name: "Starter",
      basePrice: 199,
      setupFee: 200,
      flacons: 3,
      digitalDisplay: 50,
      screenModification: 3,
      fragranceModification: 60,
      mlPerFlacon: 100,
      logoCustomization: false,
      engagement: 3,
      qrCode: true,
      reportFrequency: "mensuel"
    },
    premium: {
      name: "Premium",
      basePrice: 399,
      setupFee: 200,
      flacons: 5,
      digitalDisplay: 70,
      screenModification: 3,
      fragranceModification: 45,
      mlPerFlacon: 100,
      logoCustomization: false,
      engagement: 3,
      qrCode: true,
      reportFrequency: "mensuel"
    },
    vip: {
      name: "VIP",
      basePrice: 399,
      setupFee: 200,
      flacons: 5,
      digitalDisplay: 80,
      screenModification: 2,
      fragranceModification: 30,
      mlPerFlacon: 100,
      logoCustomization: true,
      engagement: 18,
      qrCode: true,
      reportFrequency: "mensuel"
    }
  },
  // Distribution tiers pricing
  distributionTiers: {
    essentielle: {
      id: "essentielle",
      name: "Essentielle",
      subtitle: "Entrée de gamme",
      hook: "Initiez votre présence au sein de notre réseau",
      description: "Une première exposition de votre maison dans une sélection de lieux partenaires, idéale pour tester votre diffusion et engager une audience variée.",
      features: [
        "Présence dans une sélection de lieux partenaires",
        "Visibilité standard au sein du réseau",
        "Rapport mensuel inclus"
      ],
      estimatedExposure: "2 000 – 5 000",
      exposureLabel: "Exposition indicative mensuelle",
      exposureUnit: "passages",
      microLine: "Audience large au sein du réseau partenaire",
      disclaimer: "Estimation indicative basée sur la fréquentation moyenne des lieux partenaires.",
      price: 0,
      recommended: false
    },
    performance: {
      id: "performance",
      name: "Performance",
      subtitle: "Visibilité renforcée",
      hook: "Renforcez votre présence auprès d'une audience qualifiée",
      description: "Accédez à un réseau élargi de lieux à forte fréquentation et bénéficiez d'une visibilité optimisée pour maximiser l'impact de votre diffusion.",
      features: [
        "Réseau élargi de lieux partenaires",
        "Emplacements à forte visibilité",
        "Ciblage affinitaire optimisé",
        "Rapport détaillé avec insights"
      ],
      estimatedExposure: "5 000 – 15 000",
      exposureLabel: "Exposition indicative mensuelle",
      exposureUnit: "passages",
      microLine: "Audience qualifiée avec affinité renforcée",
      disclaimer: "Estimation indicative pouvant varier selon les lieux et la période.",
      price: 150,
      recommended: true
    },
    signature: {
      id: "signature",
      name: "Signature",
      subtitle: "Diffusion premium",
      hook: "Exposez votre maison dans des environnements à forte valeur",
      description: "Intégrez les lieux les plus qualitatifs de notre réseau et bénéficiez d'une exposition ciblée auprès d'une audience premium, en parfaite affinité avec l'univers parfum.",
      features: [
        "Sélection de lieux premium",
        "Environnements à forte affinité (bien-être, lifestyle, haut de gamme)",
        "Audience qualifiée et à fort pouvoir d'achat",
        "Positionnement valorisant pour votre marque",
        "Account manager dédié"
      ],
      estimatedExposure: "8 000 – 20 000",
      exposureLabel: "Exposition premium ciblée",
      exposureUnit: "passages",
      microLine: "Audience hautement qualifiée avec forte affinité parfum",
      disclaimer: "Estimation indicative selon les emplacements, la fréquentation et les conditions d'exploitation.",
      price: 300,
      recommended: false
    }
  },
  // Add-ons - Updated for PRE-ORDER model
  addOns: {
    acceleratedDeployment: { 
      id: "acceleratedDeployment", 
      name: "Déploiement accéléré", 
      price: 100, 
      subtitle: "Priorité sur votre mise en service",
      description: "Votre projet est traité en priorité dans notre planning de déploiement, dans la limite des contraintes opérationnelles.",
      microcopy: "Mise en service estimée entre 1 et 4 mois selon disponibilités."
    },
    performancePiloting: { 
      id: "performancePiloting", 
      name: "Pilotage de performance", 
      price: 75, 
      subtitle: "Optimisez votre visibilité et vos résultats",
      description: "Accompagnement stratégique dès la phase de préparation, incluant recommandations et suivi des performances.",
      microcopy: "Inclut configuration optimisée et reporting avancé."
    }
  },
  // Pre-order configuration
  preOrderConfig: {
    estimatedDeploymentMin: 1, // months
    estimatedDeploymentMax: 4, // months
    deploymentMessage: "Mise en service estimée entre 1 et 4 mois selon disponibilités opérationnelles."
  },
  // Price per additional machine
  machinePrice: 50
};

const initialState = {
  currentStep: 1,
  selectedPackage: null,
  selectedDistributionTier: null,
  numberOfMachines: 1,
  selectedAddOns: [],
  logoFile: null,
  // Promo code
  promoCode: null,
  promoDiscount: 0,
  promoMonths: 0,
  promoApplied: false,
  // Pre-order status
  preOrderStatus: "pending",
  estimatedDeploymentWindow: "1 à 4 mois",
  acceleratedDeploymentSelected: false,
  billingInfo: {
    companyName: "",
    siret: "",
    vatNumber: "",
    legalForm: "",
    address: "",
    contactName: "",
    email: "",
    phone: ""
  },
  signatureStatus: "pending",
  paymentStatus: "pending",
  totalPrice: 0,
  monthlyPrice: 0,
  discountedPrice: 0,
  setupFee: 0
};

export const OnboardingProvider = ({ children }) => {
  // Initialize state from localStorage if available (for DocuSign/Stripe return)
  const [state, setState] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedState = localStorage.getItem('onboardingState');
      if (savedState) {
        try {
          const parsed = JSON.parse(savedState);
          // Don't restore logoFile as it's a blob URL that won't work
          return { ...parsed, logoFile: null };
        } catch (e) {
          // Error parsing saved state, use initial state
        }
      }
    }
    return initialState;
  });

  // Persist state to localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== 'undefined' && state.selectedPackage) {
      // Don't save logoFile as it contains blob URL
      const stateToSave = { ...state, logoFile: null };
      localStorage.setItem('onboardingState', JSON.stringify(stateToSave));
    }
  }, [state]);

  const calculatePricing = useCallback((currentState) => {
    const pkg = PRICING.packages[currentState.selectedPackage];
    if (!pkg) return { monthlyPrice: 0, discountedPrice: 0, setupFee: 0, totalPrice: 0 };

    // Base monthly price
    let monthlyPrice = pkg.basePrice;
    
    // Distribution tier supplement
    const tier = PRICING.distributionTiers[currentState.selectedDistributionTier];
    if (tier) {
      monthlyPrice += tier.price;
    }
    
    // Additional machines (first machine included) - same price as package
    const additionalMachines = Math.max(0, currentState.numberOfMachines - 1);
    monthlyPrice += additionalMachines * pkg.basePrice;
    
    // Add-ons
    const addOnsPrice = currentState.selectedAddOns.reduce((sum, addonId) => {
      return sum + (PRICING.addOns[addonId]?.price || 0);
    }, 0);
    monthlyPrice += addOnsPrice;
    
    const setupFee = pkg.setupFee;

    // Calculate discounted price if promo is applied
    let discountedPrice = monthlyPrice;
    if (currentState.promoApplied && currentState.promoDiscount > 0) {
      discountedPrice = monthlyPrice * (1 - currentState.promoDiscount / 100);
    }

    return {
      monthlyPrice,
      discountedPrice,
      setupFee,
      totalPrice: monthlyPrice
    };
  }, []);

  const updateState = useCallback((updates) => {
    setState(prev => {
      const newState = { ...prev, ...updates };
      const pricing = calculatePricing(newState);
      return { 
        ...newState, 
        monthlyPrice: pricing.monthlyPrice,
        discountedPrice: pricing.discountedPrice,
        setupFee: pricing.setupFee,
        totalPrice: pricing.totalPrice 
      };
    });
  }, [calculatePricing]);

  const setPackage = useCallback((pkg) => {
    updateState({ selectedPackage: pkg });
  }, [updateState]);

  const setDistributionTier = useCallback((tierId) => {
    updateState({ selectedDistributionTier: tierId });
  }, [updateState]);

  const setMachines = useCallback((count) => {
    updateState({ numberOfMachines: Math.max(1, count) });
  }, [updateState]);

  const toggleAddOn = useCallback((addonId) => {
    setState(prev => {
      const addOns = prev.selectedAddOns.includes(addonId)
        ? prev.selectedAddOns.filter(id => id !== addonId)
        : [...prev.selectedAddOns, addonId];
      const newState = { ...prev, selectedAddOns: addOns };
      const pricing = calculatePricing(newState);
      return { 
        ...newState, 
        monthlyPrice: pricing.monthlyPrice,
        setupFee: pricing.setupFee,
        totalPrice: pricing.totalPrice 
      };
    });
  }, [calculatePricing]);

  const setLogoFile = useCallback((file) => {
    updateState({ logoFile: file });
  }, [updateState]);

  const updateBillingInfo = useCallback((info) => {
    setState(prev => ({ ...prev, billingInfo: { ...prev.billingInfo, ...info } }));
  }, []);

  const setSignatureStatus = useCallback((status) => {
    updateState({ signatureStatus: status });
  }, [updateState]);

  const setPaymentStatus = useCallback((status) => {
    updateState({ paymentStatus: status });
  }, [updateState]);

  const applyPromoCode = useCallback((code, discount, months) => {
    updateState({
      promoCode: code,
      promoDiscount: discount,
      promoMonths: months,
      promoApplied: true
    });
  }, [updateState]);

  const removePromoCode = useCallback(() => {
    updateState({
      promoCode: null,
      promoDiscount: 0,
      promoMonths: 0,
      promoApplied: false
    });
  }, [updateState]);

  const nextStep = useCallback(() => {
    setState(prev => ({ ...prev, currentStep: Math.min(8, prev.currentStep + 1) }));
  }, []);

  const prevStep = useCallback(() => {
    setState(prev => ({ ...prev, currentStep: Math.max(1, prev.currentStep - 1) }));
  }, []);

  const goToStep = useCallback((step) => {
    setState(prev => ({ ...prev, currentStep: Math.min(8, Math.max(1, step)) }));
  }, []);

  const resetOnboarding = useCallback(() => {
    setState(initialState);
    // Clear localStorage
    if (typeof window !== 'undefined') {
      localStorage.removeItem('onboardingState');
      localStorage.removeItem('docusign_envelope_id');
      localStorage.removeItem('stripe_session_id');
    }
  }, []);

  const getPackageDetails = useCallback(() => {
    return PRICING.packages[state.selectedPackage] || null;
  }, [state.selectedPackage]);

  const getDistributionTierDetails = useCallback(() => {
    return PRICING.distributionTiers[state.selectedDistributionTier] || null;
  }, [state.selectedDistributionTier]);

  const value = {
    ...state,
    setPackage,
    setDistributionTier,
    setMachines,
    toggleAddOn,
    setLogoFile,
    updateBillingInfo,
    setSignatureStatus,
    setPaymentStatus,
    applyPromoCode,
    removePromoCode,
    nextStep,
    prevStep,
    goToStep,
    resetOnboarding,
    calculatePricing,
    getPackageDetails,
    getDistributionTierDetails
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error("useOnboarding must be used within OnboardingProvider");
  }
  return context;
};

export default OnboardingContext;
